import React, { useState } from 'react';
import { Modal, Button, Space, Alert, Tabs } from 'antd';
import {
  EnvironmentOutlined,
  InfoCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';

import { usePlatformConfig } from 'hooks/PlatformContext';

import './styles.scss';
import { useLocation } from 'Contexts/LocationContext';
import MapLocationPicker from './Maplocationpicker';

interface LocationModalProps {
  allowDismiss?: boolean;
  allowManualSelection?: boolean;
}

const LocationModal: React.FC<LocationModalProps> = ({
  allowDismiss = true,
  allowManualSelection = true,
}) => {
  const {
    isLocationEnabled,
    showModal,
    error,
    isChecking,
    latitude,
    longitude,
    requestLocation,
    dismissLocationRequest,
  } = useLocation();

  const platformConfig = usePlatformConfig();
  const primaryColor = platformConfig.primaryColor || '#1890ff';

  const [activeTab, setActiveTab] = useState('auto');
  const [manualLocation, setManualLocation] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // Only show modal when explicitly needed AND location is not enabled
  if (!showModal || isLocationEnabled) {
    return null;
  }

  const handleEnableLocation = async () => {
    try {
      await requestLocation();
    } catch (err) {
      console.error('Location request failed:', err);
    }
  };

  const handleManualSelection = (coords: {
    lat: number;
    lng: number;
  }) => {
    setManualLocation(coords);
    // Save manual location to context
    // You'll need to add a function in LocationContext to handle manual coords
    console.log('Manual location selected:', coords);
    dismissLocationRequest();
  };

  const handleDismiss = () => {
    if (allowDismiss) {
      dismissLocationRequest();
    }
  };

  const tabItems = [
    {
      key: 'auto',
      label: (
        <Space>
          <EnvironmentOutlined />
          Automatic
        </Space>
      ),
      children: (
        <div className="location-tab-content">
          {/* Error Alert */}
          {error && (
            <Alert
              message="Location Access Required"
              description={error}
              type="warning"
              showIcon
              icon={<InfoCircleOutlined />}
              className="location-alert"
            />
          )}

          {/* Benefits List */}
          <div className="location-benefits">
            <div className="benefit-item">
              <div
                className="benefit-icon"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <span style={{ color: primaryColor }}>✓</span>
              </div>
              <span>Prevent fraud and unauthorized access</span>
            </div>
            <div className="benefit-item">
              <div
                className="benefit-icon"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <span style={{ color: primaryColor }}>✓</span>
              </div>
              <span>Comply with financial regulations</span>
            </div>
            <div className="benefit-item">
              <div
                className="benefit-icon"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <span style={{ color: primaryColor }}>✓</span>
              </div>
              <span>Secure your transactions</span>
            </div>
          </div>

          {/* Enable Button */}
          {/*<Button
            type="primary"
            size="large"
            icon={<EnvironmentOutlined />}
            onClick={handleEnableLocation}
            loading={isChecking}
            block
            style={{
              backgroundColor: primaryColor,
              borderColor: primaryColor,
              height: '48px',
              fontSize: '16px',
              fontWeight: 500,
            }}
          >
            {isChecking ? 'Checking...' : 'Enable Location'}
          </Button>*/}

          {error && (
            <Alert
              message="Browser Permission Needed"
              description="Click 'Allow' when your browser asks for location permission. If you blocked it, please update your browser settings."
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      ),
    },
  ];

  // Add manual selection tab if allowed
  // if (allowManualSelection) {
  //   tabItems.push({
  //     key: 'manual',
  //     label: (
  //       <Space>
  //         <GlobalOutlined />
  //         Select on Map
  //       </Space>
  //     ),
  //     children: (
  //       <div className="location-tab-content">
  //         <Alert
  //           message="Manual Location Selection"
  //           description="Click on the map to select your current location. This option is available if automatic location doesn't work."
  //           type="info"
  //           showIcon
  //           style={{ marginBottom: 16 }}
  //         />
  //         <MapLocationPicker
  //           onLocationSelect={handleManualSelection}
  //           primaryColor={primaryColor}
  //         />
  //       </div>
  //     ),
  //   });
  // }

  return (
    <Modal
      open={false}
      closable={allowDismiss}
      onCancel={handleDismiss}
      footer={null}
      centered
      maskClosable={false}
      width={560}
      className="location-modal"
    >
      <div className="location-modal-content">
        {/* Icon */}
        <div
          className="location-icon"
          style={{ backgroundColor: `${primaryColor}15` }}
        >
          <EnvironmentOutlined
            style={{ fontSize: 48, color: primaryColor }}
          />
        </div>

        {/* Title */}
        <h2 className="location-title">Enable Location Access</h2>

        {/* Description */}
        <p className="location-description">
          For your security and to comply with financial regulations,
          we need to verify your location.
        </p>

        {/* Current Location Display (if available) */}
        {latitude && longitude && (
          <div className="location-coordinates">
            <InfoCircleOutlined
              style={{ color: primaryColor, marginRight: 8 }}
            />
            <span>
              Current: {latitude.toFixed(4)}, {longitude.toFixed(4)}
            </span>
          </div>
        )}

        {/* Tabs for Automatic vs Manual */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="location-tabs"
        />

        {/* Dismiss Button */}
        {allowDismiss && (
          <Button
            type="text"
            size="large"
            onClick={handleDismiss}
            block
            style={{
              height: '40px',
              color: '#8c8c8c',
              marginTop: 16,
            }}
          >
            Continue with Limited Access
          </Button>
        )}

        {/* Privacy Notice */}
        <div className="location-privacy">
          <InfoCircleOutlined
            style={{ fontSize: 12, marginRight: 6 }}
          />
          <span>
            Your location is encrypted and never shared with third
            parties
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default LocationModal;
